import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { smallcapsBold13Styles } from "@constants/typography"
import { getProductImageAlt } from "@utils/altText"
import { MobileAndTabletOnly } from "@utils/MediaQuery"
import React, { useRef, useState } from "react"
import styled, { css } from "styled-components"

import DesktopPDPMuxPlayer from "./DesktopMuxPlayer"
import { MuxVideo as MuxVideoType } from "./index"

const Container = styled.div`
  flex-direction: column;
  display: flex;
`

const List = styled.div`
  flex-wrap: nowrap;
  display: flex;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }

  @media ${breakpoints.tablet} {
    background-color: ${colors.justAnotherGray};
  }
`

const ListItem = styled.div<{ $fullWidth: boolean; $padImage: boolean }>`
  ${({ $fullWidth, $padImage }) => css`
    flex-grow: 0;
    flex-shrink: 0;
    display: inline-flex;
    width: ${$fullWidth ? "100vw" : "calc(100vw - 24px)"};
    height: auto;
    margin-right: 4px;
    padding: ${$padImage ? "24px" : "0"};
    background-color: ${colors.backgroundGray};

    @media ${breakpoints.tablet} {
      width: ${$fullWidth ? "100vw" : "calc(8.5 / 12 * 100vw)"};
      height: auto;
      margin-right: 8px;
      padding: padding: ${$padImage ? "48px" : "0"};
    }
  `}
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ImageWrapper = styled(({ action, ...props }) => <div {...props} />)<any>`
  ${(action) => css`
    width: 100%;
    height: auto;

    ${ImageHelperContainer} {
      height: auto;
      width: 100%;
      background: ${colors.backgroundGray};

      img {
        object-fit: ${action ? "cover" : "contain"} !important;
      }
    }
  `}
`

const TabBar = styled.div`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  display: flex;
  border-top: 1px solid ${colors.disabledGray};
`

const Tab = styled.button<{ $active?: boolean }>`
  ${smallcapsBold13Styles}
  flex-grow: 0;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  position: relative;
  display: flex;
  opacity: ${(props) => (props.$active ? 1 : 0.3)};
  width: 50%;
  height: 52.26px;
  border: none;
  background: ${colors.disabledGray};
  outline: none;
  color: ${colors.black};
  cursor: pointer;

  ::before {
    content: "";
    position: absolute;
    top: 0;
    transform: rotate(135deg) translateY(60%);
    opacity: ${(props) => (props.$active ? 1 : 0)};
    width: 12.97px;
    height: 12.97px;
    background: ${colors.disabledGray};
  }
`

interface MobileProductImagesProps {
  currentVariant: {
    images: Array<string>
    name?: string
  }
  deviceName?: string
  imageGalleryMuxVideos?: Array<MuxVideoType>
  images: Array<string>
  lifestyleMuxVideos?: Array<MuxVideoType>
  productName?: string
}

const MobileProductImages = (props: MobileProductImagesProps) => {
  const listRef = useRef<HTMLDivElement>(null)
  const [activeTab, setActiveTab] = useState(0)
  const { currentVariant } = props

  const variantImages = currentVariant?.images || []
  const actionImages = props.images || []

  const firstSetOfVariantImages = [...variantImages].splice(0, 2) || []
  const restOfVariantImages = [...variantImages].splice(2) || []
  const galleryMuxVideos = props.imageGalleryMuxVideos || []
  const setItInActionMuxVideos = props.lifestyleMuxVideos || []

  const media = [
    ...firstSetOfVariantImages,
    ...galleryMuxVideos,
    ...restOfVariantImages,
    ...actionImages,
    ...setItInActionMuxVideos,
  ]

  const onClickTab = (tab: number) => {
    if (listRef.current) {
      if (tab === 0) {
        listRef.current.scrollTo({ behavior: "smooth", left: 0 })
      } else if (tab === 1) {
        listRef.current.scrollTo({
          behavior: "smooth",
          left:
            listRef.current.scrollWidth -
            listRef.current.scrollHeight * actionImages.length -
            25,
        })
      }
    }
  }

  const handleListScroll = React.useCallback((e) => {
    const { offsetHeight, scrollLeft } = e.currentTarget

    const totalWidthOfVariantImages = offsetHeight * variantImages.length
    if (scrollLeft > totalWidthOfVariantImages - offsetHeight / 2) {
      setActiveTab(1)
    } else {
      setActiveTab(0)
    }
  }, [])

  return (
    <MobileAndTabletOnly>
      <Container>
        <List onScroll={handleListScroll} ref={listRef}>
          {media.map((mediaItem, index) => (
            <ListItem
              $fullWidth={media.length === 1}
              $padImage={index < variantImages.length}
              key={index}
            >
              {typeof mediaItem === "string" ? (
                <ImageWrapper action={index >= variantImages.length}>
                  <ImageHelper
                    alt={getProductImageAlt({
                      colorName: props.currentVariant?.name,
                      deviceName: props.deviceName,
                      imageType:
                        index >= variantImages.length ? "lifestyle" : "gallery",
                      productName: props.productName || "Case",
                    })}
                    src={mediaItem}
                  />
                </ImageWrapper>
              ) : (
                <DesktopPDPMuxPlayer muxVideo={mediaItem} />
              )}
            </ListItem>
          ))}
        </List>
        {(actionImages.length > 0 || setItInActionMuxVideos?.length > 0) && (
          <TabBar>
            <Tab $active={activeTab === 0} onClick={() => onClickTab(0)}>
              Detail Shots
            </Tab>
            <Tab $active={activeTab === 1} onClick={() => onClickTab(1)}>
              In Action
            </Tab>
          </TabBar>
        )}
      </Container>
    </MobileAndTabletOnly>
  )
}

export default MobileProductImages
